import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Grid,
  Chip,
  Autocomplete,
  Stack,
  Tabs,
  Tab,
  InputAdornment,
  Collapse,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FilterList as FilterIcon,
  FilterListOff as FilterListOffIcon,
  Print as PrintIcon,
  RestoreFromTrash as RestoreIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from '@mui/icons-material';
import { stockExitsNewAPI } from '../services/api';
import { getModuleTableTheme } from '../theme/tableTheme';
import { StockExitNew } from '../types/stockExitNew';
import AlertDialog from './AlertDialog';
import StockExitNewAdd from './StockExitNewAdd';
import StockExitNewEdit from './StockExitNewEdit';
import StockExitPaymentsTab from './StockExitPaymentsTab';
import StockExitPaymentSummaryTab from './StockExitPaymentSummaryTab';
import useAlert from '../hooks/useAlert';
import { extractErrorMessage } from '../utils/errorHandler';
import usePdfPreview from '../hooks/usePdfPreview';
import { Article } from '../types';
import { Client } from '../types/client';
import { Demandeur } from '../types/demandeur';
import { Service } from '../types/service';
import { Projet } from '../types/projet';
import ConfirmationDialog, { ConfirmationField } from './ConfirmationDialog';
import CustomTablePagination from './CustomTablePagination';
import PermissionButton from './PermissionButton';
import TruncatedTextWithTooltip from './TruncatedTextWithTooltip';
import ColumnFilter from './ColumnFilter';
import { useColumnFilters } from '../hooks/useColumnFilters';
// generateStockExitPDF is dynamically imported in the print handler so the
// jspdf chunk only loads when the user clicks Print.
import { auditLogsAPI } from '../services/api';
import {
  useStockExitsList,
  useDeletedStockExits,
  useInvalidateStockExits,
} from '../hooks/queries/useStockExits';
import {
  useArticlesDropdown,
  useClientsDropdown,
  useDemandeursDropdown,
  useServicesDropdown,
  useProjetsDropdown,
} from '../hooks/queries/useDropdowns';

const STOCK_COLOR = '#2196F3';

const EXIT_FILTER_SCHEMA = {
  bon_number: { type: 'text', param: 'bon_number' },
  client_id:  { type: 'autocomplete', param: 'client_id' },
  is_paid:    { type: 'select', param: 'is_paid' },
} as const;

const EXIT_PAID_OPTIONS = [
  { value: 'true', label: 'Payé' },
  { value: 'false', label: 'Non payé' },
];

const StockExitNewManagement: React.FC = () => {
  const formatNumber = (value: any, decimals: number = 2): string => {
    return Number(value || 0).toFixed(decimals);
  };

  const formatCurrency = (value: any): string => {
    return `${formatNumber(value)} MAD`;
  };

  const { data: articles = [] } = useArticlesDropdown();
  const { data: clients = [] } = useClientsDropdown();
  const { data: demandeurs = [] } = useDemandeursDropdown();
  const { data: services = [] } = useServicesDropdown();
  const { data: projets = [] } = useProjetsDropdown();
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [formMinimized, setFormMinimized] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingExit, setEditingExit] = useState<StockExitNew | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [tabValue, setTabValue] = useState(0);
  const { alertState, showError, showSuccess, showConfirm, hideAlert } = useAlert();
  const { openPreview, pdfPreviewElement } = usePdfPreview();

  const [selectedExitIds, setSelectedExitIds] = useState<Set<number>>(new Set());

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterStartDate, setFilterStartDate] = useState<string>('');
  const [filterEndDate, setFilterEndDate] = useState<string>('');
  const [filterarticleId, setFilterarticleId] = useState<number | null>(null);
  const [filterServiceId, setFilterServiceId] = useState<number | null>(null);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] = useState(false);
  const [exitToDelete, setExitToDelete] = useState<StockExitNew | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const { filters, update, reset, toQueryParams } = useColumnFilters(EXIT_FILTER_SCHEMA, {
    onChange: () => setPage(0),
  });
  const clientOptions = clients.map((c) => ({ value: String(c.id), label: c.name }));

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Cached + live list: auto-refreshes when any user changes a stock exit
  // (WebSocket invalidation), and revisiting the same page/filters is instant.
  const listParams = useMemo(() => {
    const extra = toQueryParams();
    return {
      skip: page * rowsPerPage,
      limit: rowsPerPage,
      search: debouncedSearch || undefined,
      startDate: filterStartDate || undefined,
      endDate: filterEndDate || undefined,
      articleId: filterarticleId || undefined,
      serviceId: filterServiceId || undefined,
      extra: Object.keys(extra).length > 0 ? extra : undefined,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, debouncedSearch, filterStartDate, filterEndDate, filterarticleId, filterServiceId, filters]);

  const exitsQuery = useStockExitsList(listParams, tabValue === 0);
  const deletedQuery = useDeletedStockExits(tabValue === 3);
  const invalidateExits = useInvalidateStockExits();

  const stockExits = exitsQuery.data?.items ?? [];
  const totalCount = exitsQuery.data?.total ?? 0;
  const deletedStockExits = deletedQuery.data ?? [];
  const loading =
    tabValue === 0 ? exitsQuery.isInitialLoading
    : tabValue === 3 ? deletedQuery.isInitialLoading
    : false;

  const allExitsSelected = stockExits.length > 0 && selectedExitIds.size === stockExits.length;
  const someExitsSelected = selectedExitIds.size > 0 && !allExitsSelected;

  const toggleExitSelect = (id: number) => {
    setSelectedExitIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleAllExits = () => {
    setSelectedExitIds(allExitsSelected ? new Set() : new Set(stockExits.map((e) => e.id!).filter(Boolean)));
  };

  const handlePrintSelectedExits = () => {
    const chosen = stockExits.filter((e) => e.id && selectedExitIds.has(e.id));
    if (chosen.length === 0) return;
    openPreview({
      getBlob: async () => {
        const { generateStockExitsBatchPDF } = await import('../utils/pdfGenerator');
        return generateStockExitsBatchPDF(chosen, true) as Blob;
      },
      fileName: `Bons_Sortie_${new Date().toISOString().split('T')[0]}.pdf`,
    });
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0);
    setExpandedRows(new Set());
  };

  const handleRestore = async (exit: StockExitNew) => {
    if (!exit.id) return;
    showConfirm(
      `Êtes-vous sûr de vouloir restaurer la sortie de stock BON "${exit.bon_number}" ?`,
      async () => {
        try {
          await stockExitsNewAPI.restore(exit.id!);
          showSuccess('Sortie de stock restaurée avec succès');
          invalidateExits();
        } catch (err) {
          showError('Erreur lors de la restauration de la sortie');
        }
      },
      {
        title: 'Confirmer la restauration',
        confirmText: 'Restaurer',
        cancelText: 'Annuler'
      }
    );
  };

  const handleOpenAddDialog = () => {
    setFormMinimized(false);
    setOpenAddDialog(true);
  };

  const handleOpenEditDialog = (stockExit: StockExitNew) => {
    setEditingExit(stockExit);
    setOpenEditDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setFormMinimized(false);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setEditingExit(null);
  };

  const handleDeleteClick = (exit: StockExitNew) => {
    setExitToDelete(exit);
    setShowDeleteConfirmDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (!exitToDelete || !exitToDelete.id) return;

    setIsDeleting(true);
    try {
      await stockExitsNewAPI.delete(exitToDelete.id);
      showSuccess(`BON supprimé avec succès (${exitToDelete.items.length} article(s))`);
      setShowDeleteConfirmDialog(false);
      setExitToDelete(null);
      invalidateExits();
    } catch (err: any) {
      showError(extractErrorMessage(err));
    } finally {
      setIsDeleting(false);
    }
  };

  const getDeleteConfirmationFields = (): ConfirmationField[] => {
    if (!exitToDelete) return [];

    const fields: ConfirmationField[] = [];

    fields.push({ label: 'Date', value: exitToDelete.date ? new Date(exitToDelete.date).toLocaleDateString('fr-FR') : '-' });
    fields.push({ label: 'BON N°', value: exitToDelete.bon_number });

    if (exitToDelete.client_name) {
      fields.push({ label: 'Client', value: exitToDelete.client_name });
    }
    if (exitToDelete.demandeur_name) {
      fields.push({ label: 'Demandeur', value: exitToDelete.demandeur_name });
    }
    if (exitToDelete.service_name) {
      fields.push({ label: 'Service', value: exitToDelete.service_name });
    }

    fields.push({ label: '---', value: '---' });
    fields.push({ label: 'Articles', value: `${exitToDelete.items.length}`, bold: true });

    // Add all articles
    exitToDelete.items.forEach((item, index) => {
      fields.push({
        label: `Article ${index + 1}`,
        value: `${item.article_reference} - ${item.article_designation}`,
      });
      fields.push({
        label: `  Quantité`,
        value: `${formatNumber(item.quantity, 2)}`,
      });
      fields.push({
        label: `  Prix Sortie`,
        value: `${formatNumber(item.prix_sortie)} MAD`,
      });
      fields.push({
        label: `  Total`,
        value: `${formatNumber(item.total)} MAD`,
      });
    });

    // Grand total
    fields.push({ label: '---', value: '---' });
    fields.push({
      label: 'TOTAL GÉNÉRAL',
      value: `${formatNumber(exitToDelete.grand_total)} MAD`,
      bold: true,
    });

    return fields;
  };

  const handleFilter = () => {
    setPage(0);
  };

  const handleClearFilters = () => {
    setFilterStartDate('');
    setFilterEndDate('');
    setFilterarticleId(null);
    setFilterServiceId(null);
    setSearchTerm('');
    reset();
    setPage(0);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setExpandedRows(new Set());
    setSelectedExitIds(new Set());
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setExpandedRows(new Set());
  };

  const toggleRowExpansion = (exitId: number) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(exitId)) {
        newSet.delete(exitId);
      } else {
        newSet.add(exitId);
      }
      return newSet;
    });
  };

  return (
    <Box p={3} sx={{ width: '100%', maxWidth: 'none' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">
          Sorties de Stock
        </Typography>
        {tabValue === 0 && (
          <PermissionButton
            moduleName="Stock"
            operation="add"
            miniModuleName="Stock Exit"
            variant="contained"
            onClick={handleOpenAddDialog}
            startIcon={<AddIcon />}
            sx={{
              backgroundColor: '#2196F3',
              '&:hover': {
                backgroundColor: '#1976D2',
              }
            }}
          >
            Nouvelle Sortie
          </PermissionButton>
        )}
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label={`Sorties Actives (${totalCount})`} />
          <Tab label="Paiements" />
          <Tab label="Récapitulatif Paiements" />
          <Tab label="Corbeille" />
        </Tabs>
      </Box>

      {tabValue === 0 && (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <TextField
              variant="outlined"
              placeholder="Rechercher par BON N° ou article..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ minWidth: 350 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={handlePrintSelectedExits}
              disabled={selectedExitIds.size === 0}
              sx={{ backgroundColor: STOCK_COLOR, '&:hover': { backgroundColor: '#1976D2' } }}
            >
              Imprimer la sélection ({selectedExitIds.size})
            </Button>
          </Box>

          <Paper sx={{ p: 2, mb: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <TextField
                  variant="filled"
                  label="Date début"
                  type="date"
                  value={filterStartDate}
                  onChange={(e) => setFilterStartDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  variant="filled"
                  label="Date fin"
                  type="date"
                  value={filterEndDate}
                  onChange={(e) => setFilterEndDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Autocomplete
                  options={articles || []}
                  getOptionLabel={(option) => `${option.reference} - ${option.designation}`}
                  value={(articles || []).find(a => a.id === filterarticleId) || null}
                  onChange={(_, article) => setFilterarticleId(article?.id || null)}
                  renderInput={(params) => <TextField {...params} label="Article" variant="filled" />}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth variant="filled">
                  <InputLabel>Service</InputLabel>
                  <Select
                    value={filterServiceId || ''}
                    onChange={(e) => setFilterServiceId(e.target.value as number || null)}
                  >
                    <MenuItem value="">Tous</MenuItem>
                    {(services || []).map((service) => (
                      <MenuItem key={service.id} value={service.id}>
                        {service.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={<FilterIcon />}
                  onClick={handleFilter}
                  sx={{ mr: 1 }}
                >
                  Filtrer
                </Button>
                <Button variant="outlined" onClick={handleClearFilters}>
                  Effacer les filtres
                </Button>
              </Grid>
            </Grid>
          </Paper>

          <TableContainer component={Paper} sx={{ ...getModuleTableTheme('stock'), width: '100%', maxWidth: 'none' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allExitsSelected}
                      indeterminate={someExitsSelected}
                      onChange={toggleAllExits}
                      disabled={stockExits.length === 0}
                    />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', width: 50 }}></TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Date</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      BON N°
                      <ColumnFilter type="text" value={filters.bon_number} onChange={(v) => update('bon_number', v)} label="BON N°" color={STOCK_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Client
                      <ColumnFilter type="autocomplete" value={filters.client_id} onChange={(v) => update('client_id', v)} options={clientOptions} label="client" color={STOCK_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Service</TableCell>
                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>Articles</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Total</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Avance / Reste</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      Payé
                      <ColumnFilter type="select" value={filters.is_paid} onChange={(v) => update('is_paid', v)} options={EXIT_PAID_OPTIONS} label="statut paiement" color={STOCK_COLOR} />
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      Chargement...
                    </TableCell>
                  </TableRow>
                ) : stockExits.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      Aucune sortie de stock trouvée
                    </TableCell>
                  </TableRow>
                ) : (
                  stockExits.map((exit) => {
                    const isExpanded = expandedRows.has(exit.id!);
                    return (
                      <React.Fragment key={exit.id}>
                        <TableRow
                          hover
                          selected={!!exit.id && selectedExitIds.has(exit.id)}
                          sx={{
                            cursor: 'pointer',
                            '& > *': { borderBottom: isExpanded ? 'unset' : undefined },
                          }}
                          onClick={() => toggleRowExpansion(exit.id!)}
                        >
                          <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={!!exit.id && selectedExitIds.has(exit.id)}
                              onChange={() => exit.id && toggleExitSelect(exit.id)}
                            />
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <IconButton size="small">
                              {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                            </IconButton>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {exit.date ? new Date(exit.date).toLocaleDateString('fr-FR') : '-'}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>
                            {exit.bon_number}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap', maxWidth: 200 }}>
                            <TruncatedTextWithTooltip text={exit.client_name} maxWidth={200} />
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {exit.service_name && (
                              <Chip label={exit.service_name} size="small" color="primary" variant="filled" />
                            )}
                          </TableCell>
                          <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                            <Chip label={exit.items?.length || 0} size="small" color="info" />
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            <strong>{formatCurrency(exit.grand_total)}</strong>
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            <div>{formatCurrency(exit.total_paid)}</div>
                            <Typography
                              variant="caption"
                              sx={{ fontWeight: 600, color: Number(exit.remaining || 0) > 0 ? '#f44336' : '#4caf50' }}
                            >
                              Reste {formatCurrency(exit.remaining)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <Chip
                              label={exit.is_paid ? 'Oui' : 'Non'}
                              size="small"
                              color={exit.is_paid ? 'success' : 'warning'}
                            />
                          </TableCell>
                          <TableCell align="center" sx={{ whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                            <Stack direction="row" spacing={1} justifyContent="center">
                              <PermissionButton
                                moduleName="Stock"
                                operation="view"
                                miniModuleName="Stock Exit"
                                component="IconButton"
                                onClick={() => {
                                  openPreview({
                                    getBlob: async () => {
                                      const { generateStockExitPDF } = await import('../utils/pdfGenerator');
                                      return (await generateStockExitPDF(exit, true)) as Blob;
                                    },
                                    fileName: `${exit.bon_number}.pdf`,
                                  });
                                  auditLogsAPI.logPdfDownload('StockExit', exit.id || 0, `Aperçu PDF - ${exit.bon_number}`);
                                }}
                                size="small"
                                sx={{
                                  color: '#2196F3',
                                  '&:hover': {
                                    backgroundColor: 'rgba(33, 150, 243, 0.1)',
                                  }
                                }}
                                title="Imprimer"
                              >
                                <PrintIcon />
                              </PermissionButton>
                              <PermissionButton
                                moduleName="Stock"
                                operation="edit"
                                miniModuleName="Stock Exit"
                                component="IconButton"
                                onClick={() => handleOpenEditDialog(exit)}
                                size="small"
                              >
                                <EditIcon />
                              </PermissionButton>
                              <PermissionButton
                                moduleName="Stock"
                                operation="delete"
                                miniModuleName="Stock Exit"
                                component="IconButton"
                                onClick={() => handleDeleteClick(exit)}
                                size="small"
                                color="error"
                              >
                                <DeleteIcon />
                              </PermissionButton>
                            </Stack>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={11}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ margin: 2 }}>
                                <Typography variant="subtitle2" gutterBottom component="div" sx={{ fontWeight: 'bold' }}>
                                  Détails des articles
                                </Typography>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow sx={{ backgroundColor: 'background.default' }}>
                                      <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>Référence</TableCell>
                                      <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>Désignation</TableCell>
                                      <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>Catégorie</TableCell>
                                      <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>Unité</TableCell>
                                      <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>Quantité</TableCell>
                                      <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>Prix Sortie</TableCell>
                                      <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>Total</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {(exit.items || []).map((item, index) => (
                                      <TableRow key={item.id || index}>
                                        <TableCell sx={{ whiteSpace: 'nowrap', maxWidth: 150 }}>
                                          <TruncatedTextWithTooltip text={item.article_reference} maxWidth={150} />
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap', maxWidth: 200 }}>
                                          <TruncatedTextWithTooltip text={item.article_designation} maxWidth={200} />
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                          {item.category && <Chip label={item.category} size="small" variant="outlined" />}
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{item.unit || '-'}</TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatNumber(item.quantity, 2)}</TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatCurrency(item.prix_sortie)}</TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                          <strong>{formatCurrency(item.total)}</strong>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomTablePagination
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}

      {tabValue === 1 && <StockExitPaymentsTab />}

      {tabValue === 2 && <StockExitPaymentSummaryTab />}

      {tabValue === 3 && (
        <>
          <TableContainer component={Paper} sx={{ ...getModuleTableTheme('stock'), width: '100%', maxWidth: 'none' }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ whiteSpace: 'nowrap', width: 50 }}></TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Date</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>BON N°</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Client</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Service</TableCell>
                  <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>Articles</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Total</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Date de Suppression</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      Chargement...
                    </TableCell>
                  </TableRow>
                ) : deletedStockExits.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      Aucune sortie de stock supprimée
                    </TableCell>
                  </TableRow>
                ) : (
                  deletedStockExits.map((exit) => {
                    const isExpanded = expandedRows.has(exit.id!);
                    return (
                      <React.Fragment key={exit.id}>
                        <TableRow
                          hover
                          sx={{
                            cursor: 'pointer',
                            '& > *': { borderBottom: isExpanded ? 'unset' : undefined },
                          }}
                          onClick={() => toggleRowExpansion(exit.id!)}
                        >
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <IconButton size="small">
                              {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                            </IconButton>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {exit.date ? new Date(exit.date).toLocaleDateString('fr-FR') : '-'}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <strong>{exit.bon_number}</strong>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 150, whiteSpace: 'nowrap' }}>
                            <TruncatedTextWithTooltip text={exit.client_name} maxWidth={150} />
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {exit.service_name && (
                              <Chip label={exit.service_name} size="small" color="primary" variant="filled" />
                            )}
                          </TableCell>
                          <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                            <Chip label={exit.total_articles || exit.items.length} size="small" color="info" />
                          </TableCell>
                          <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                            <strong>{formatNumber(exit.grand_total)} MAD</strong>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {exit.deleted_at ? new Date(exit.deleted_at).toLocaleDateString('fr-FR') : '-'}
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                            <IconButton
                              onClick={() => handleRestore(exit)}
                              color="primary"
                              size="small"
                              title="Restaurer"
                            >
                              <RestoreIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={11}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ margin: 2 }}>
                                <Typography variant="subtitle2" gutterBottom component="div" sx={{ fontWeight: 'bold' }}>
                                  Détails des articles
                                </Typography>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow sx={{ backgroundColor: 'background.default' }}>
                                      <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>Référence</TableCell>
                                      <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>Désignation</TableCell>
                                      <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>Catégorie</TableCell>
                                      <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>Unité</TableCell>
                                      <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>Quantité</TableCell>
                                      <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>Prix Sortie</TableCell>
                                      <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}>Total</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {(exit.items || []).map((item, index) => (
                                      <TableRow key={item.id || index}>
                                        <TableCell sx={{ whiteSpace: 'nowrap', maxWidth: 150 }}>
                                          <TruncatedTextWithTooltip text={item.article_reference} maxWidth={150} />
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap', maxWidth: 200 }}>
                                          <TruncatedTextWithTooltip text={item.article_designation} maxWidth={200} />
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                          {item.category && <Chip label={item.category} size="small" variant="outlined" />}
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{item.unit || '-'}</TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatNumber(item.quantity, 2)}</TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatCurrency(item.prix_sortie)}</TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                          <strong>{formatCurrency(item.total)}</strong>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomTablePagination
            count={deletedStockExits.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </>
      )}

      {/* Add Dialog */}
      <StockExitNewAdd
        open={openAddDialog}
        onClose={handleCloseAddDialog}
        minimized={formMinimized}
        onMinimizedChange={setFormMinimized}
        onSuccess={invalidateExits}
        articles={articles}
        clients={clients}
        demandeurs={demandeurs}
        services={services}
        projets={projets}
        showError={showError}
        showSuccess={showSuccess}
      />

      {/* Edit Dialog */}
      <StockExitNewEdit
        open={openEditDialog}
        onClose={handleCloseEditDialog}
        onSuccess={invalidateExits}
        stockExit={editingExit}
        articles={articles}
        clients={clients}
        demandeurs={demandeurs}
        services={services}
        projets={projets}
        showError={showError}
        showSuccess={showSuccess}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={showDeleteConfirmDialog}
        title="Confirmer la suppression"
        message="Cette action est irréversible. Êtes-vous sûr de vouloir supprimer ce BON de sortie avec tous ses articles ?"
        fields={getDeleteConfirmationFields()}
        movementType="exit"
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          setShowDeleteConfirmDialog(false);
          setExitToDelete(null);
        }}
        confirmText="Supprimer"
        cancelText="Annuler"
        loading={isDeleting}
      />

      <AlertDialog
        open={alertState.open}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        onConfirm={alertState.onConfirm}
        onClose={hideAlert}
      />

      {/* PDF Preview (print/download from the viewer toolbar) */}
      {pdfPreviewElement}
    </Box>
  );
};

export default StockExitNewManagement;
